// ── Signal Engine v6 (Frontend, ESM) ─────────────────────────────────────────
// Sync mit: server/signalEngine.js (CJS) — Single Source of Truth ist der Server
// v6: Growth-Pfad, Coverage-Normalization, AI-Aufwertung, Dip-Quality-Check

import SECTOR_MAP from '../config/sectorMap.js';
import SECTOR_VALUATION from '../config/sectorValuation.js';

const MAX_FACTORS = 14;

// ── Signal-Tuning (v6.5.1) — Server-env SIGNAL_BUY_THRESHOLD/SIGNAL_ATH_CAP/SIGNAL_ATH_PENALTY_FLOOR haben Vorrang.
// Frontend-Fallback hartkodiert: Display-Parity nur relevant wenn Client ohne Server scored.
const SIGNAL_BUY_THRESHOLD = 15;
const SIGNAL_ATH_CAP = 85;
const SIGNAL_ATH_PENALTY_FLOOR = 78;

function calcRSI(closes, period = 14) {
  if (!closes || closes.length < period + 2) return null;
  const changes = [];
  for (let i = 1; i < closes.length; i++) changes.push(closes[i] - closes[i - 1]);
  const recent  = changes.slice(-period);
  const avgGain = recent.filter(c => c > 0).reduce((s, v) => s + v, 0) / period;
  const avgLoss = recent.filter(c => c < 0).reduce((s, v) => s + Math.abs(v), 0) / period;
  if (avgLoss === 0) return 100;
  return parseFloat((100 - 100 / (1 + avgGain / avgLoss)).toFixed(1));
}

function calcEMA(closes, period) {
  if (!closes || closes.length < period) return null;
  const k = 2 / (period + 1);
  let ema = closes.slice(0, period).reduce((s, v) => s + v, 0) / period;
  for (let i = period; i < closes.length; i++) {
    ema = closes[i] * k + ema * (1 - k);
  }
  return ema;
}

function calcTrendStrength(closes, days = 10) {
  if (!closes || closes.length < days + 1) return null;
  const recent = closes.slice(-days - 1);
  let up = 0, down = 0;
  for (let i = 1; i < recent.length; i++) {
    if (recent[i] > recent[i - 1]) up++;
    else if (recent[i] < recent[i - 1]) down++;
  }
  const total = up + down;
  if (total === 0) return 0;
  return (up - down) / total;
}

function calcSMA(closes, period) {
  if (!closes || closes.length < period) return null;
  return closes.slice(-period).reduce((s, v) => s + v, 0) / period;
}

function calcMACD(closes) {
  if (!closes || closes.length < 35) return null;
  const ema12 = calcEMA(closes, 12);
  const ema26 = calcEMA(closes, 26);
  if (ema12 === null || ema26 === null) return null;
  const macdLine = ema12 - ema26;

  const prevEma12 = calcEMA(closes.slice(0, -1), 12);
  const prevEma26 = calcEMA(closes.slice(0, -1), 26);
  const prevMacd = (prevEma12 && prevEma26) ? prevEma12 - prevEma26 : null;

  return {
    macdLine,
    prevMacd,
    crossingUp: prevMacd !== null && prevMacd < 0 && macdLine > 0,
    crossingDown: prevMacd !== null && prevMacd > 0 && macdLine < 0,
    bullish: macdLine > 0,
  };
}

export function calcSignal(change24h_, change7d_, extras = {}) {
  const change24h = change24h_ ?? 0;
  const change7d  = change7d_  ?? 0;
  const {
    closes, week52Low, week52High, currentPrice, avgVolume, lastVolume,
    recommendationMean, numberOfAnalystOpinions, targetMeanPrice,
    newsSentiment, newsImpact, trailingPE, priceToCashflow, aiPick, sector,
    // v6: neue Felder
    forwardPE, marketRegime, sectorRotation, revenueTrend, netIncomeTrend,
  } = extras;

  const rsi = calcRSI(closes);

  const w52Range = (week52High && week52Low) ? week52High - week52Low : 0;
  const w52Pos   = (w52Range > 0 && currentPrice)
    ? ((currentPrice - week52Low) / w52Range) * 100
    : null;

  const volSpike = (avgVolume && lastVolume && avgVolume > 0)
    ? lastVolume / avgVolume : null;

  const ema8  = calcEMA(closes, 8);
  const ema21 = calcEMA(closes, 21);
  const trendStrength = calcTrendStrength(closes);
  const sma50  = calcSMA(closes, 50);
  const sma200 = calcSMA(closes, 200);
  const macd   = calcMACD(closes);

  let buyScore  = 0;
  let sellScore = 0;
  let factorCount = 0;
  const buyReasons  = [];
  const sellReasons = [];

  // ── Growth-Score vorab berechnen ──
  const analystCount = numberOfAnalystOpinions ?? 0;
  let analystUpside = null;
  if (targetMeanPrice != null && currentPrice > 0) {
    analystUpside = ((targetMeanPrice - currentPrice) / currentPrice) * 100;
  }

  let growthScore = 0;
  if (aiPick?.signal?.toUpperCase() === 'KAUFEN') growthScore++;
  if (revenueTrend === 1) growthScore++;
  if (netIncomeTrend === 1) growthScore++;
  if (forwardPE && trailingPE && forwardPE < trailingPE * 0.85) growthScore++;
  if (analystUpside !== null && analystUpside >= 15 && analystCount >= 3) growthScore++;
  if (sma200 !== null && currentPrice > sma200) growthScore++;
  if (sectorRotation?.leading?.includes(sector)) growthScore++;
  const isGrowth = growthScore >= 4;

  // ── 1. Kursbewegung (v6: Dip-Gewichte reduziert) ──
  if (change7d <= -5)  { buyScore  += 2; buyReasons.push(`7d-Rücksetzer ${change7d.toFixed(1)}% – Einstiegschance`); factorCount++; }
  if (change7d <= -10) { buyScore  += 2; buyReasons.push('Starker Rückgang – erhöhtes Erholungspotenzial'); }
  if (change7d >= 10)  {
    sellScore += 2;
    sellReasons.push(`+${change7d.toFixed(1)}% in 7 Tagen – technische Korrektur möglich`);
    factorCount++;
  }

  if (change24h >= 10) {
    sellScore += 2;
    sellReasons.push(`+${change24h.toFixed(1)}% heute – kurzfristig überhitzt, Rücksetzer abwarten`);
    factorCount++;
  } else if (change24h <= -8 && change7d <= -15) {
    buyScore += 2;
    buyReasons.push(`Negativer Momentum-Break: ${change24h.toFixed(1)}% heute & ${change7d.toFixed(1)}% 7d – mögliche Überreaktion`);
    factorCount++;
  }

  if (change24h >= 2 && change24h < 10 && change7d >= 2) {
    buyScore += 1; buyReasons.push(`Positiver Tagesimpuls: +${change24h.toFixed(1)}%`); factorCount++;
  }
  if (change24h <= -4 && change24h > -8) {
    sellScore += 1; sellReasons.push(`Schwache Tagsentwicklung: ${change24h.toFixed(1)}%`); factorCount++;
  }

  // ── 2. RSI (v6: Dip-Gewichte reduziert) ──
  if (rsi !== null) {
    factorCount++;
    if (rsi < 25)      { buyScore  += 4; buyReasons.push(`RSI ${rsi} – extrem überverkauft, Trendwende wahrscheinlich`); }
    else if (rsi < 32) { buyScore  += 2; buyReasons.push(`RSI ${rsi} – stark überverkauft`); }
    else if (rsi < 40) { buyScore  += 1; buyReasons.push(`RSI ${rsi} – überverkauft`); }
    if (rsi > 78)      { sellScore += 4; sellReasons.push(`RSI ${rsi} – stark überkauft, Gewinnmitnahmen wahrscheinlich`); }
    else if (rsi > 70) {
      sellScore += 2;
      sellReasons.push(`RSI ${rsi} – überkauft`);
    }
  }

  // ── 3. 52-Wochen-Nähe (v6: Dip reduziert, Growth-Dämpfung) ──
  if (w52Pos !== null) {
    factorCount++;
    if (w52Pos < 8)       { buyScore  += 3; buyReasons.push(`Nur ${w52Pos.toFixed(0)}% über 52W-Tief – historisch günstiger Einstieg`); }
    else if (w52Pos < 18) { buyScore  += 2; buyReasons.push(`Nahe 52-Wochen-Tief (${w52Pos.toFixed(0)}% darüber)`); }
    if (w52Pos > 92) {
      if (isGrowth) {
        sellScore += 2;
        sellReasons.push('Nahe 52W-Hoch – auch bei Wachstum erhöhtes Rückschlagrisiko');
      } else {
        sellScore += 3;
        sellReasons.push('Nahe 52-Wochen-Hoch – begrenztes Aufwärtspotenzial');
      }
    } else if (w52Pos > 82) { sellScore += 1; sellReasons.push('Im oberen Bereich des 52W-Niveaus'); }
  }

  // ── 4. Volumen-Spike ──
  if (volSpike !== null && volSpike > 2.0 && change7d <= -5) {
    buyScore += 2;
    buyReasons.push(`${volSpike.toFixed(1)}x Volumen-Spike – Panikverkäufe, Erholung möglich`);
    factorCount++;
  }

  // ── 5. Analysten-Kursziel (v6: jetzt mit Score!) ──
  if (analystUpside !== null) {
    factorCount++;
    if (analystUpside >= 30 && analystCount >= 5) {
      buyScore += 3;
      buyReasons.push(`Analysten Kursziel +${analystUpside.toFixed(0)}% (${analystCount} Analysten) – breiter Konsens`);
    } else if (analystUpside >= 15 && analystCount >= 3) {
      buyScore += 2;
      buyReasons.push(`Analysten Kursziel +${analystUpside.toFixed(0)}% (${analystCount} Analysten)`);
    } else if (analystUpside >= 5 && analystCount >= 2) {
      buyScore += 1;
      buyReasons.push(`Analysten Kursziel +${analystUpside.toFixed(0)}%`);
    } else if (analystUpside <= -15 && analystCount >= 3) {
      sellScore += 2;
      sellReasons.push(`Analysten Kursziel ${analystUpside.toFixed(0)}% – Abwärtspotenzial`);
    } else if (analystUpside <= -5 && analystCount >= 2) {
      sellScore += 1;
      sellReasons.push(`Analysten Kursziel ${analystUpside.toFixed(0)}%`);
    }
  }

  // ── 5b. Analysten-Empfehlung (v6: neu) ──
  if (recommendationMean != null && analystCount >= 3) {
    factorCount++;
    if (recommendationMean <= 1.5 && analystCount >= 5) {
      buyScore += 2;
      buyReasons.push(`Starker Kauf-Konsens (Ø ${recommendationMean.toFixed(1)}, ${analystCount} Analysten)`);
    } else if (recommendationMean <= 2.0) {
      buyScore += 1;
      buyReasons.push(`Kauf-Konsens (Ø ${recommendationMean.toFixed(1)}, ${analystCount} Analysten)`);
    } else if (recommendationMean >= 4.0) {
      sellScore += 2;
      sellReasons.push(`Verkauf-Konsens (Ø ${recommendationMean.toFixed(1)}, ${analystCount} Analysten)`);
    } else if (recommendationMean >= 3.5) {
      sellScore += 1;
      sellReasons.push(`Untergewichten-Konsens (Ø ${recommendationMean.toFixed(1)}, ${analystCount} Analysten)`);
    }
  }

  // ── 6. EMA-Crossover ──
  if (ema8 !== null && ema21 !== null && currentPrice > 0) {
    factorCount++;
    const emaDiff = ((ema8 - ema21) / ema21) * 100;
    if (Math.abs(emaDiff) < 0.5 && currentPrice > ema8) {
      buyScore += 1;
      buyReasons.push('EMA-Crossover bullish – möglicher Trendwechsel nach oben');
    } else if (Math.abs(emaDiff) < 0.5 && currentPrice < ema8) {
      sellScore += 1;
      sellReasons.push('EMA-Crossover bearish – möglicher Trendwechsel nach unten');
    }
  }

  // ── 6b. 200-Tage-MA ──
  if (sma200 !== null && currentPrice > 0) {
    factorCount++;
    if (currentPrice > sma200) {
      buyScore += 1;
      buyReasons.push('Über 200-Tage-Durchschnitt – langfristiger Aufwärtstrend');
    } else {
      sellScore += 1;
      sellReasons.push('Unter 200-Tage-Durchschnitt – langfristiger Abwärtstrend');
    }
  }

  // ── 6c. Golden Cross ──
  if (sma50 !== null && sma200 !== null) {
    const sma50Diff = ((sma50 - sma200) / sma200) * 100;
    if (sma50Diff > 0 && sma50Diff < 2) {
      buyScore += 2;
      buyReasons.push('Golden Cross – starkes bullisches Langfrist-Signal');
    }
  }

  // ── 6d. MACD + RSI ──
  if (macd !== null && rsi !== null) {
    factorCount++;
    if (macd.crossingUp && rsi < 50) {
      buyScore += 2;
      buyReasons.push('MACD-Crossover bullish + RSI bestätigt');
    } else if (macd.crossingDown && rsi > 50) {
      sellScore += 2;
      sellReasons.push('MACD-Crossover bearish + RSI bestätigt');
    } else if (macd.bullish && rsi < 45) {
      buyScore += 1;
      buyReasons.push('MACD positiv, RSI zeigt Aufholpotenzial');
    }
  }

  // ── 7. Trend-Stärke ──
  if (trendStrength !== null && Math.abs(trendStrength) >= 0.6) {
    const label = trendStrength > 0
      ? `Aufwärtstrend (${Math.round(trendStrength * 100)}% positive Tage)`
      : `Abwärtstrend (${Math.round(Math.abs(trendStrength) * 100)}% negative Tage)`;
    (trendStrength > 0 ? buyReasons : sellReasons).push(label);
  }

  // ── 8. News-Sentiment (v6: symmetrisch) ──
  const impactMult = newsImpact === 'high' ? 1.5 : (newsImpact === 'medium' ? 1.0 : 0.5);
  const newsBlock = newsSentiment !== null && newsSentiment <= -0.5;
  if (newsSentiment !== null && newsSentiment !== undefined) {
    factorCount++;
    if (newsSentiment >= 0.5) {
      buyScore += Math.min(Math.round(3 * impactMult), 4);
      buyReasons.push(`KI-News: positiv (${(newsSentiment * 100).toFixed(0)}%)`);
    } else if (newsSentiment >= 0.2) {
      buyScore += 1;
      buyReasons.push('KI-News: leicht positiv');
    } else if (newsSentiment <= -0.7) {
      sellScore += Math.min(Math.round(4 * impactMult), 5);
      sellReasons.push('KI-News: stark negativ');
    } else if (newsSentiment <= -0.5) {
      sellScore += Math.min(Math.round(3 * impactMult), 4);
      sellReasons.push('KI-News: negativ');
    } else if (newsSentiment <= -0.3) {
      sellScore += Math.min(Math.round(2 * impactMult), 2);
      sellReasons.push('KI-News: leicht negativ');
    }
  }

  // ── 9. KGV & KCV ──
  const sectorLabel = sector || 'Unbekannt';
  const sectorVal = SECTOR_VALUATION[sector] || SECTOR_VALUATION['default'];

  if (trailingPE != null && trailingPE > 0) {
    factorCount++;
    if (trailingPE < sectorVal.pe_cheap) {
      buyScore += 2;
      buyReasons.push(`KGV ${trailingPE.toFixed(0)} – günstig für ${sectorLabel}`);
    } else if (trailingPE < sectorVal.pe_fair_max) {
      buyScore += 1;
      buyReasons.push(`KGV ${trailingPE.toFixed(0)} – fair für ${sectorLabel}`);
    } else if (trailingPE > sectorVal.pe_expensive) {
      sellScore += 2;
      sellReasons.push(`KGV ${trailingPE.toFixed(0)} – sehr teuer für ${sectorLabel}`);
    } else if (trailingPE > sectorVal.pe_fair_max) {
      sellScore += 1;
      sellReasons.push(`KGV ${trailingPE.toFixed(0)} – ambitioniert für ${sectorLabel}`);
    }
  }
  if (priceToCashflow != null && priceToCashflow > 0) {
    factorCount++;
    if (priceToCashflow < sectorVal.pcf_cheap) {
      buyScore += 3;
      buyReasons.push(`KCV ${priceToCashflow.toFixed(0)} – starker Cashflow für ${sectorLabel}`);
    } else if (priceToCashflow < sectorVal.pcf_fair_max) {
      buyScore += 1;
      buyReasons.push(`KCV ${priceToCashflow.toFixed(0)} – solider Cashflow`);
    } else if (priceToCashflow > sectorVal.pcf_expensive) {
      sellScore += 1;
      sellReasons.push(`KCV ${priceToCashflow.toFixed(0)} – schwacher Cashflow für ${sectorLabel}`);
    }
  }

  // ── 9b. Forward-PE (v6: neu) ──
  if (forwardPE != null && forwardPE > 0 && trailingPE != null && trailingPE > 0) {
    factorCount++;
    if (forwardPE < trailingPE * 0.7 && forwardPE < sectorVal.pe_fair_max) {
      buyScore += 2;
      buyReasons.push(`Forward-KGV ${forwardPE.toFixed(0)} vs. ${trailingPE.toFixed(0)} – starkes Gewinnwachstum erwartet`);
    } else if (forwardPE < trailingPE * 0.85) {
      buyScore += 1;
      buyReasons.push(`Forward-KGV ${forwardPE.toFixed(0)} – Gewinnwachstum erwartet`);
    } else if (forwardPE > trailingPE * 1.3) {
      sellScore += 1;
      sellReasons.push(`Forward-KGV ${forwardPE.toFixed(0)} vs. ${trailingPE.toFixed(0)} – Gewinnrückgang erwartet`);
    }
  }

  // ── 10. KI-Analyse (v6: aufgewertet) ──
  if (aiPick) {
    const aiSignal = aiPick.signal?.toUpperCase();
    const horizon = aiPick.horizon ? ` (${aiPick.horizon})` : '';
    const riskLevel = (aiPick.riskLevel || 'mittel').toLowerCase();
    if (aiSignal === 'KAUFEN') {
      const aiScore = riskLevel === 'niedrig' ? 4 : (riskLevel === 'mittel' ? 3 : 2);
      buyScore += aiScore;
      buyReasons.unshift(`KI-Analyse: ${aiPick.reason}${horizon}`);
      factorCount++;
    } else if (aiSignal === 'VERKAUFEN') {
      sellScore += 4;
      sellReasons.unshift(`KI-Analyse: ${aiPick.reason}${horizon}`);
      factorCount++;
    } else if (aiSignal === 'HALTEN') {
      buyReasons.push(`KI-Analyse: ${aiPick.reason}${horizon}`);
    }
  }

  // ── 10b. AI-Synergie ──
  if (aiPick?.signal?.toUpperCase() === 'KAUFEN') {
    let synergy = 0;
    if (revenueTrend === 1) synergy++;
    if (forwardPE && trailingPE && forwardPE < trailingPE * 0.85) synergy++;
    if (analystUpside !== null && analystUpside >= 15 && analystCount >= 3) synergy++;
    if (recommendationMean != null && recommendationMean <= 2.0 && analystCount >= 3) synergy++;
    if (synergy >= 2) {
      buyScore += 2;
      buyReasons.push('KI-Analyse durch Fundamentals bestätigt');
    }
  }

  // ── 11. Revenue & Income Trend (v6: neu) ──
  if (revenueTrend != null || netIncomeTrend != null) {
    factorCount++;
    if (revenueTrend === 1 && netIncomeTrend === 1) {
      buyScore += 2;
      buyReasons.push('Umsatz UND Gewinn steigend – starkes Wachstum');
    } else if (revenueTrend === 1) {
      buyScore += 1;
      buyReasons.push('Umsatzwachstum');
    } else if (revenueTrend === -1 && netIncomeTrend === -1) {
      sellScore += 2;
      sellReasons.push('Umsatz UND Gewinn rückläufig');
    } else if (netIncomeTrend === -1) {
      sellScore += 1;
      sellReasons.push('Gewinn rückläufig');
    }
  }

  // ── 12. Sektor-Rotation (v6: neu) ──
  if (sectorRotation && sector) {
    if (sectorRotation.leading?.includes(sector)) {
      buyScore += 1;
      buyReasons.push(`Sektor ${sectorLabel} im Aufwind`);
    } else if (sectorRotation.lagging?.includes(sector) &&
               marketRegime?.riskAppetite === 'risk_off') {
      sellScore += 1;
      sellReasons.push(`Sektor ${sectorLabel} unter Druck im Risk-Off-Umfeld`);
    }
  }

  // ── 13. Growth-Bonus (v6: neu) ──
  if (growthScore >= 5) {
    buyScore += 2;
    buyReasons.push('Starkes Wachstumsprofil – multiple Faktoren bestätigen Potenzial');
  } else if (growthScore >= 4) {
    buyScore += 1;
    buyReasons.push('Solides Wachstumsprofil');
  }

  // ── 14. Dip-Quality-Check (v6: neu) ──
  const isDipActive = (rsi !== null && rsi < 32) || (w52Pos !== null && w52Pos < 18) || change7d <= -5;
  if (isDipActive) {
    const hasFundament =
      (aiPick?.signal?.toUpperCase() === 'KAUFEN' || aiPick?.signal?.toUpperCase() === 'HALTEN') ||
      (recommendationMean != null && recommendationMean <= 2.5 && analystCount >= 3) ||
      (revenueTrend != null && revenueTrend >= 0);
    if (!hasFundament) {
      buyScore -= 2;
      buyReasons.push('Dip ohne fundamentale Absicherung – erhöhtes Risiko');
    }
  }

  // ── ATH Entry Penalty (v6.5) ──
  if (w52Pos !== null && w52Pos > SIGNAL_ATH_PENALTY_FLOOR && w52Pos <= SIGNAL_ATH_CAP) {
    buyScore -= 2;
  }

  // ── Falling-Knife-Schutz ──
  const isFallingKnife = (
    change7d < -15 &&
    recommendationMean !== null &&
    recommendationMean >= 3.5 &&
    analystCount >= 3
  ) || (
    change7d < -25 && change24h < -5
  );

  // ── Entscheidung ──
  let signal, reasons, confidence, fallingKnife = false;

  if (isFallingKnife) {
    fallingKnife = true;
    signal       = 'Halten';
    confidence   = 40;
    const knifeReasons = [];
    if (change7d < -25) {
      knifeReasons.push(`Extremer Kursverfall: ${change7d.toFixed(1)}% in 7 Tagen`);
    } else {
      knifeReasons.push(`Möglicher Falling Knife: ${change7d.toFixed(1)}% Rückgang bei negativem Analysten-Konsens`);
    }
    if (analystCount >= 3 && recommendationMean != null) {
      knifeReasons.push(`${analystCount} Analysten raten überwiegend zum Verkauf (Ø ${recommendationMean.toFixed(1)} / 5)`);
    }
    if (rsi !== null) {
      knifeReasons.push(`RSI ${rsi} – technisch überverkauft, aber fundamentale Risiken beachten`);
    }
    reasons = knifeReasons;
  } else if (buyScore >= SIGNAL_BUY_THRESHOLD && buyScore > sellScore && !newsBlock) {
    if (volSpike !== null && volSpike > 1.5) {
      buyScore += 1;
      buyReasons.push(`Volumen ${volSpike.toFixed(1)}x bestätigt Kaufsignal`);
    }
    signal  = 'Kaufen';
    reasons = buyReasons.length ? buyReasons : [`7d ${change7d.toFixed(1)}% – Einstieg geprüft`];

    const coverageRatio = Math.min(factorCount / MAX_FACTORS, 1.0);
    const coverageMultiplier = 0.6 + 0.4 * coverageRatio;
    let volAdjustment = 0;
    if (volSpike !== null && volSpike < 0.7) {
      volAdjustment = -8;
      reasons.push('Schwaches Volumen – Signal weniger verlässlich');
    }
    confidence = Math.round((50 + buyScore * 4) * coverageMultiplier + volAdjustment);
    if (coverageRatio < 0.5) confidence = Math.min(confidence, 80);
    if (coverageRatio < 0.7) confidence = Math.min(confidence, 90);
    confidence = Math.min(confidence, 94);
  } else if (sellScore >= 3 && sellScore > buyScore) {
    signal  = 'Verkaufen';
    reasons = sellReasons.length ? sellReasons : ['Technische Überhitzung'];
    const coverageRatio = Math.min(factorCount / MAX_FACTORS, 1.0);
    const coverageMultiplier = 0.6 + 0.4 * coverageRatio;
    confidence = Math.round((50 + sellScore * 4) * coverageMultiplier);
    if (coverageRatio < 0.5) confidence = Math.min(confidence, 80);
    if (coverageRatio < 0.7) confidence = Math.min(confidence, 90);
    confidence = Math.min(confidence, 91);
  } else {
    signal     = 'Halten';
    reasons    = buildHoldReasons(change24h, change7d, rsi, w52Pos, trendStrength);
    confidence = Math.min(55 + Math.abs(buyScore - sellScore) * 2 + Math.min(factorCount, 4), 85);
  }

  // ── ATH-Schutz (v6.5.1) ──
  if (signal === 'Kaufen' && w52Pos !== null) {
    if (w52Pos > SIGNAL_ATH_CAP) {
      signal = 'Halten';
      reasons = [`52W-Position ${w52Pos.toFixed(0)}% – zu nah am Allzeithoch für Kaufempfehlung`];
      if (isGrowth) reasons.push('Wachstumsprofil intakt, aber Einstieg riskant');
      confidence = Math.min(confidence, 70);
    } else if (w52Pos > SIGNAL_ATH_PENALTY_FLOOR) {
      confidence = Math.min(confidence, 75);
      reasons.push(`52W-Position ${w52Pos.toFixed(0)}% – Confidence gedeckelt, oberes Preisband`);
    }
  }

  // ── Entry-Pflicht: Kaufen nur bei echtem Einstiegs-Trigger (v6.5) ──
  if (signal === 'Kaufen') {
    const hasEntryTrigger =
      (rsi !== null && rsi < 50) ||
      (w52Pos !== null && w52Pos < 65) ||
      change7d <= -5 ||
      (analystUpside !== null && analystUpside >= 20);
    if (!hasEntryTrigger) {
      signal = 'Halten';
      reasons = ['Fundamental stark, aber kein günstiger Einstiegspunkt – RSI neutral, Kurs im oberen Bereich'];
      confidence = Math.min(confidence, 68);
    }
  }

  // ── Confidence-Cap (v6: erweitert) ──
  if (confidence > 75 && signal !== 'Halten') {
    const maxSingleFactor = Math.max(
      rsi !== null && rsi < 25 ? 4 : (rsi !== null && rsi < 32 ? 2 : 0),
      w52Pos !== null && w52Pos < 8 ? 3 : (w52Pos !== null && w52Pos < 18 ? 2 : 0),
      macd?.crossingUp && rsi < 50 ? 2 : 0,
      macd?.crossingDown && rsi > 50 ? 2 : 0,
      aiPick?.signal?.toUpperCase() === 'KAUFEN' ? (aiPick.riskLevel === 'niedrig' ? 4 : 3) : 0,
      aiPick?.signal?.toUpperCase() === 'VERKAUFEN' ? 4 : 0,
      trailingPE != null && trailingPE < sectorVal.pe_cheap ? 2 : 0,
      priceToCashflow != null && priceToCashflow < sectorVal.pcf_cheap ? 3 : 0,
      revenueTrend === 1 && netIncomeTrend === 1 ? 2 : 0,
      analystUpside !== null && analystUpside >= 30 && analystCount >= 5 ? 3 : 0,
      growthScore >= 5 ? 2 : 0,
    );
    if (maxSingleFactor < 3) {
      confidence = 75;
      reasons.push('Confidence gedeckelt – kein starker Einzelindikator');
    }
  }

  // ── Market-Regime-Modifier (v6: neu) ──
  if (marketRegime && signal === 'Kaufen') {
    if (marketRegime.riskAppetite === 'risk_off' && trailingPE > sectorVal.pe_expensive) {
      confidence = Math.max(confidence - 10, 50);
      reasons.push('Risk-Off Umfeld – teure Aktien unter Druck');
    }
    if (marketRegime.sentiment === 'extreme_fear' && isDipActive) {
      confidence = Math.min(confidence + 5, 94);
      reasons.push('Extreme Fear – Contrarian-Signal verstärkt');
    }
  }

  const risk = Math.abs(change24h) >= 5 || Math.abs(change7d) >= 12 ? 'Hoch'
    : Math.abs(change24h) >= 2 || Math.abs(change7d) >= 5 ? 'Mittel' : 'Niedrig';

  return {
    signal, reasons, confidence, fallingKnife, risk, rsi,
    week52Position: w52Pos !== null ? parseFloat(w52Pos.toFixed(1)) : null,
  };
}

function buildHoldReasons(change24h, change7d, rsi, w52Pos, trendStrength) {
  const r = [];
  if (rsi !== null)    r.push(`RSI ${rsi} – neutrale Zone`);
  if (w52Pos !== null) r.push(`52W-Position: ${w52Pos.toFixed(0)}%`);
  if (trendStrength !== null && Math.abs(trendStrength) < 0.3) {
    r.push('Seitwärtsbewegung – kein klarer Trend');
  }
  if (!r.length) r.push(change7d >= 0
    ? `+${change7d.toFixed(1)}% in 7 Tagen – kein Signal-Trigger`
    : `${change7d.toFixed(1)}% in 7 Tagen – kein klarer Einstieg`);
  return r;
}

export function buildAsset(meta, priceData, eurUsd = 1.08, aiPick = null) {
  const { ticker, name, type } = meta;
  const {
    price, change24h, change7d, currency,
    closes, week52Low, week52High, avgVolume, lastVolume,
    recommendationMean, numberOfAnalystOpinions, targetMeanPrice, recommendationKey,
    sector, industry,
    trailingPE, forwardPE, priceToBook, priceToCashflow, marketCap, dividendYield,
  } = priceData;

  if (price == null || price <= 0) return null;
  const priceEur = currency === 'USD' ? price / eurUsd : price;

  const resolvedSector = sector ?? SECTOR_MAP[ticker]?.sector ?? null;

  const { signal, reasons, confidence, risk, rsi, week52Position, fallingKnife } = calcSignal(
    change24h, change7d,
    {
      closes, week52Low, week52High, currentPrice: price,
      avgVolume, lastVolume,
      recommendationMean:      recommendationMean      ?? null,
      numberOfAnalystOpinions: numberOfAnalystOpinions ?? null,
      targetMeanPrice:         targetMeanPrice         ?? null,
      newsSentiment:           priceData.newsSentiment  ?? null,
      newsImpact:              priceData.newsImpact     ?? null,
      trailingPE:              trailingPE               ?? null,
      priceToCashflow:         priceToCashflow          ?? null,
      aiPick,
      sector:                  resolvedSector,
      forwardPE:               forwardPE                ?? null,
    }
  );

  return {
    ticker,
    name:                    priceData.name || name,
    price:                   parseFloat(priceEur.toFixed(priceEur < 1 ? 4 : 2)),
    change:                  change24h,
    change7d,
    currency:                'EUR',
    signal,
    confidence,
    risk,
    reasons,
    rsi,
    week52Position,
    fallingKnife:            fallingKnife ?? false,
    recommendationMean:      recommendationMean      ?? null,
    numberOfAnalystOpinions: numberOfAnalystOpinions ?? null,
    targetMeanPrice:         targetMeanPrice         ?? null,
    recommendationKey:       recommendationKey       ?? null,
    nextAction:              nextActionFromSignal(signal, risk, fallingKnife),
    source:                  priceData.source,
    type:                    type || (priceData.source === 'coingecko' ? 'crypto' : 'stock'),
    liveData:                true,
    closes:                  closes ? closes.slice(-7) : null,
    sector:                  resolvedSector,
    industry:                industry ?? SECTOR_MAP[ticker]?.industry ?? null,
    trailingPE:              trailingPE       ?? null,
    forwardPE:               forwardPE        ?? null,
    priceToBook:             priceToBook      ?? null,
    priceToCashflow:         priceToCashflow  ?? null,
    marketCap:               marketCap        ?? null,
    dividendYield:           dividendYield    ?? null,
  };
}

function nextActionFromSignal(signal, risk, fallingKnife) {
  if (fallingKnife)           return 'Fundamentale Lage prüfen – Nachrichten lesen';
  if (signal === 'Kaufen')    return risk === 'Hoch' ? 'Nur mit engem Stop-Loss einsteigen' : 'Gestaffelten Einstieg prüfen';
  if (signal === 'Verkaufen') return risk === 'Hoch' ? 'Position reduzieren, Stop-Loss prüfen' : 'Teilgewinn mitnehmen';
  return 'Breakout-Alert setzen';
}
